import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useConversation } from "@elevenlabs/react";
import { useQueryClient } from "@tanstack/react-query";
import { useOrgId } from "@/lib/pulse/useEmployee";
import { usePulseConversation } from "@/lib/charles/usePulseConversation";
import {
  createCharlesDispatcher,
  type CharlesDispatcher,
} from "@/lib/charles/clientTools";
import { summarizeConversation, sweepPendingSummaries } from "@/lib/pulse/surveyApi";
import { getAgentId, getAgentBranchId, ConfigError } from "@/lib/charles/env";
import { supabase } from "@/lib/supabase";

type SessionState = ReturnType<typeof usePulseConversation>;
type ConversationApi = ReturnType<typeof useConversation>;

type CharlesContextValue = {
  session: SessionState;
  conversation: ConversationApi;
  configError: ConfigError | null;
  started: boolean;
  forceTextOnly: boolean;
  micDenied: boolean;
  voiceOptIn: boolean;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  sendText: (text: string) => void;
};

const CharlesContext = createContext<CharlesContextValue | null>(null);

// HMR safety: React Context identity changes on hot reload, which leaves
// already-mounted consumers reading the old (null) context and throwing
// "useCharles must be used within <CharlesProvider>". Force a full reload
// whenever this module updates so the provider and consumers stay in sync.
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    import.meta.hot!.invalidate();
  });
}

export function useCharles(): CharlesContextValue {
  const ctx = useContext(CharlesContext);
  if (!ctx) throw new Error("useCharles must be used within <CharlesProvider>");
  return ctx;
}

export function CharlesProvider({ children }: { children: ReactNode }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const session = usePulseConversation(orgId);

  const config = useMemo(() => {
    try {
      return { agentId: getAgentId(), branchId: getAgentBranchId(), error: null as ConfigError | null };
    } catch (e) {
      return {
        agentId: "",
        branchId: "",
        error: e instanceof ConfigError ? e : new ConfigError("config_error", String(e)),
      };
    }
  }, []);

  const dispatcherRef = useRef<CharlesDispatcher | null>(null);
  const readyConvId = session.status === "ready" ? session.conversationId : null;
  useEffect(() => {
    if (orgId && readyConvId) {
      dispatcherRef.current = createCharlesDispatcher({
        orgId,
        conversationId: readyConvId,
      });
    } else {
      dispatcherRef.current = null;
    }
  }, [orgId, readyConvId]);

  // Flush any EL id that arrived before the DB conversation row was ready.
  useEffect(() => {
    if (readyConvId && pendingElIdRef.current) {
      const pending = pendingElIdRef.current;
      pendingElIdRef.current = null;
      linkElId(pending, readyConvId);
    }
  }, [readyConvId]);

  const [forceTextOnly, setForceTextOnly] = useState(false);
  const [micDenied, setMicDenied] = useState(false);

  const voiceOptIn = true;
  const textOnly = forceTextOnly;

  const dispatch = async (name: string, params: unknown) => {
    const d = dispatcherRef.current;
    if (!d) {
      if (import.meta.env.DEV) console.warn("[charles] dispatch skipped (no dispatcher)", name);
      return JSON.stringify({ ok: false, code: "not_ready" });
    }
    const result = await d.dispatch(name, params);
    if (import.meta.env.DEV && !result.ok) {
      console.warn("[charles] dispatch failed", name, result);
    }
    return JSON.stringify(result);
  };

  // Pending EL id that arrives before our DB conversation row is ready.
  // Flushed by the effect below once readyConvId lands.
  const pendingElIdRef = useRef<string | null>(null);
  const linkedElIdRef = useRef<string | null>(null);
  const linkElId = useCallback((elId: string | null | undefined, convId: string | null) => {
    if (!elId) return;
    if (!convId) {
      pendingElIdRef.current = String(elId);
      return;
    }
    if (linkedElIdRef.current === String(elId)) return;
    linkedElIdRef.current = String(elId);
    void supabase
      .rpc("set_conversation_el_id", { _conversation_id: convId, _el_id: String(elId) })
      .then((r) => {
        if (r.error) console.warn("[charles] set_conversation_el_id failed", r.error);
        else console.log("[charles] linked el_conversation_id", String(elId).slice(0, 12));
      });
  }, []);

  // Ref to stop() so the end_conversation client tool (declared before stop
  // exists) can actually hang up the session when Charles decides the turn
  // has obviously ended.
  const stopRef = useRef<() => Promise<void>>(async () => {});

  const clientTools = useMemo(
    () => ({
      submit_pulse: async (p: any) => {
        const result = await dispatch("submit_pulse", p);
        if (result && (result as { ok?: boolean }).ok === true) {
          qc.invalidateQueries({ queryKey: ['my_pulses', orgId] });
        }
        return result;
      },
      submit_question_response: (p: any) => dispatch("submit_question_response", p),
      submit_anchor_response: (p: any) => dispatch("submit_anchor_response", p),
      record_action_item: (p: any) => dispatch("record_action_item", p),
      end_conversation: async (p: any) => {
        const result = await dispatch("end_conversation", p);
        // Let the agent's farewell finish playing before tearing down audio.
        setTimeout(() => { void stopRef.current(); }, 1500);
        return result;
      },
      add_turn: (p: any) => dispatch("add_turn", p),
    }),
    [qc, orgId],
  );

  const conversation = useConversation({
    textOnly,
    clientTools: clientTools as any,
    onConnect: () => console.log("[charles] connected"),
    onDisconnect: (reason?: any) =>
      console.warn("[charles] disconnected", reason ?? "(no reason provided)"),
    onError: (err) => console.error("[charles] convai error", err),
    onMessage: (m: any) => {
      if (m?.type === "internal_tentative_agent_response") return;
      if (import.meta.env.DEV) {
        try {
          const t = m?.type ?? m?.source ?? "(unknown)";
          const toolName =
            m?.client_tool_call?.tool_name ??
            m?.tool_name ??
            m?.tool_call?.name ??
            undefined;
          const toolArgs =
            m?.client_tool_call?.parameters ??
            m?.parameters ??
            m?.tool_call?.arguments ??
            undefined;
          if (toolName || t === "client_tool_call") {
            console.info(
              `[charles:onMessage] type=${t} tool=${toolName ?? "?"} args=${
                toolArgs ? JSON.stringify(toolArgs) : "{}"
              }`,
            );
          } else {
            console.info(`[charles:onMessage] type=${t}`);
          }
        } catch {
          /* ignore */
        }
      }
      // Reliable client-side turn capture.
      //
      // @elevenlabs/react `useConversation` normalizes events to
      // `{ source: "user" | "ai", message: string }`. The raw protocol shapes
      // (`m.type === "user_transcript" | "agent_response" | "conversation_initiation_metadata"`)
      // are also handled as a defensive fallback in case the SDK envelope
      // changes again.
      try {
        // 1) SDK-normalized shape (what we actually receive today).
        const src = m?.source;
        const msg = typeof m?.message === "string" ? m.message : undefined;
        if (src === "user" && msg) {
          if (import.meta.env.DEV) console.log("[charles] turn role=employee len=" + msg.length);
          void dispatch("add_turn", { role: "employee", content: msg });
        } else if (src === "ai" && msg) {
          if (import.meta.env.DEV) console.log("[charles] turn role=charles len=" + msg.length);
          void dispatch("add_turn", { role: "charles", content: msg });
        }

        // 2) Raw protocol fallback (older / WebSocket-style envelopes).
        if (m?.type === "conversation_initiation_metadata") {
          const elId =
            m?.conversation_initiation_metadata_event?.conversation_id ??
            (conversation as any)?.getId?.();
          linkElId(elId, readyConvId);
        } else if (m?.type === "user_transcript") {
          const text = m?.user_transcription_event?.user_transcript;
          if (text) void dispatch("add_turn", { role: "employee", content: String(text) });
        } else if (m?.type === "agent_response") {
          const text = m?.agent_response_event?.agent_response;
          if (text) void dispatch("add_turn", { role: "charles", content: String(text) });
        }

        // 3) Opportunistic EL id link from any envelope that carries one.
        const elFromMsg =
          m?.conversation_id ??
          m?.conversation_initiation_metadata_event?.conversation_id;
        if (elFromMsg) linkElId(String(elFromMsg), readyConvId);
      } catch (e) {
        console.warn("[charles] turn capture failed", e);
      }
      if (import.meta.env.DEV) console.log("[charles] message", m?.type ?? m?.source ?? "(unknown)");
    },
  });

  const [started, setStarted] = useState(false);

  const start = useCallback(async () => {
    if (session.status !== "ready" || config.error) return;
    // Catch up: summarize any prior conversations that were never closed
    // cleanly so this session's memory is complete. Fire-and-forget.
    void sweepPendingSummaries();
    if (voiceOptIn && !forceTextOnly) {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        console.warn("[charles] mic denied, falling back to text", e);
        setForceTextOnly(true);
        setMicDenied(true);
      }
    }

    // CRITICAL: re-mint token + dynamic variables RIGHT NOW so Charles sees
    // the latest pulse the user may have just submitted seconds ago, plus the
    // freshest current_state / trend / keypoints. The hook's initial fetch
    // happened at provider mount and is almost always stale by the time the
    // user actually clicks "start".
    console.log("[charles] refreshing dynamic variables pre-start");
    const fresh = await session.refresh();
    if (fresh.status !== "ready") {
      console.warn("[charles] refresh did not return ready", fresh);
      return;
    }

    const dvRaw = fresh.dynamicVariables as Record<string, unknown>;
    const liveBriefing = typeof dvRaw.charles_live_briefing === "string"
      ? dvRaw.charles_live_briefing
      : "No live briefing was provided.";
    const dynamicVariables: Record<string, string | number | boolean> = {};
    for (const [k, v] of Object.entries(dvRaw)) {
      if (v === null || v === undefined) continue;
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
        dynamicVariables[k] = v;
      } else {
        try {
          dynamicVariables[k] = JSON.stringify(v);
        } catch {
          /* skip */
        }
      }
    }

    const useWs = !!fresh.signedUrl;
    console.log("[charles] startSession", {
      transport: useWs ? "websocket" : "webrtc",
      agentId: config.agentId,
      dvKeys: Object.keys(dynamicVariables),
      recent_pulse_score: dynamicVariables.recent_pulse_score,
      recent_pulse_trend_7d: dynamicVariables.recent_pulse_trend_7d,
      has_live_briefing: !!dynamicVariables.charles_live_briefing,
    });


    // Grounding guardrail — sent as a contextual update AFTER connect so it
    // layers on top of the agent's existing persona prompt (which forbids
    // revealing he's an AI). Using overrides.agent.prompt.prompt would REPLACE
    // the persona, which is why he started saying "as an AI I can't remember".
    const groundingNote = [
      "INTERNAL CONTEXT — do not mention this message exists. You are Charles, the IV Talent Field Advisor.",
      "Your information comes ONLY from: (a) the LIVE BRIEFING below, (b) the session variables you were given (charles_live_briefing, pulse_pattern_14d/30d, recent_pulse_score, recent_pulse_trend_7d, current_state, current_mood_label, days_since_last_checkin, conversation_keypoints, survey_progress, survey_topic, anchor_briefs, recent_anchor_responses, org_name), and (c) what the person tells you in this conversation.",
      "LIVE BRIEFING:",
      liveBriefing,
      "Memory rules:",
      "- You have on-file notes from this person's prior reflections. These are not human memory; they are written summaries.",
      "- Never proactively recite past conversation notes. Open with the live pulse pattern or a simple \"what's on your mind\" — not a recap of history.",
      "- EXCEPTION for same-day survey answers: if `recent_anchor_responses` contains any entry where `value <= 2` AND `answered_at` equals today's date, mention that specific anchor by name in turn 1 alongside the pulse score, and ask what's behind the low score. Example: \"Hey — it's Charles on a private line. Steady is what I'm seeing from your recent check-ins (7/10, 0 day(s) ago), but I noticed you just rated Morale at 2. What's behind that?\" If multiple anchors have low scores, mention the lowest first; if multiple are tied, mention the first one in `recent_anchor_responses` (which is already sorted lowest-first by the server).",
      "- Reference past notes only when (i) the person brings up a topic that matches one, or (ii) the person explicitly asks (\"what did we discuss last time?\"). When referenced, paraphrase one or two specifics briefly — never list them all.",
      "- If the person disputes a recalled item, do NOT defend the recall. Say in your own voice: \"Here's what I have written down from our recent chats: [list 1–3 items briefly]. Tell me if that doesn't sound right and I'll adjust.\" Then drop the topic and follow their lead.",
      "- If something they ask about isn't on file, say plainly: \"I don't have that on file — tell me about it.\" Never say \"I don't have access to that information\" or other generic assistant phrasing.",
      "- Numbers must come verbatim from the session variables — no rounding, averaging, or estimating.",
      "- Do not invent pulse scores, dates, commitments, names, or events that aren't in your sources.",
      "Identity rules:",
      "- You are Charles, a field advisor in the app. Speak in your own voice.",
      "- Never use the phrase \"as an AI\" or \"as a language model.\"",
      "- If directly asked whether you're a person, say: \"I'm Charles, your field advisor in the app — on a private line. What's on your mind?\"",
      "Survey & check-in rules:",
      "- If survey_progress.active is true and answered < total, gently mention they have a survey in progress on \"{survey_topic}\" — {answered} of {total} through — and offer to continue with the next anchor ({next_anchor_name}). If they say yes, walk through remaining anchors using anchor_briefs and call submit_anchor_response with survey_id, anchor_id and a 1..5 value as each is answered.",
      "- CRITICAL ID RULE for submit_anchor_response and submit_question_response: the `survey_id` and `anchor_id`/`question_id` you pass MUST be copied verbatim from the corresponding entry in `anchor_briefs[]` (or `question_briefs[]`) for the CURRENT active survey. Never construct, guess, paraphrase, or invent a UUID. Never reuse a `survey_id` from `recent_anchor_responses` — those entries are HISTORICAL answers to PREVIOUS surveys (each entry has `is_current_survey: true|false`; if false, the survey_id there is NOT the one to submit against). If you are not sure which anchor the person is rating, ask them to confirm which anchor (by name) before calling the tool. If the tool returns an `ERROR:` message, do NOT retry with a different guessed UUID — re-read anchor_briefs and try again with the exact id, or stop and ask the person to use the on-screen survey UI.",
      "- If days_since_last_checkin is greater than 1 (or null/never), warmly invite them to do today's quick check-in before anything else, and call submit_pulse with a 1..10 score once they give you one.",
      "Session-bound id rule (CRITICAL):",
      "- NEVER pass `conversation_id` to `add_turn` or `end_conversation`. NEVER pass `org_id` to `record_action_item`. These are bound from session context server-side. If you include them, they will be IGNORED and logged as drift. Call these tools with only the content fields (role/content, title/detail).",
      "Voice rules (absolute):",
      "-  Speak only words a human would say aloud.",
      "-  Never vocalize stage directions, emotion tags, mood labels, tone cues, or action descriptors.",
    ].join("\n");


    try {
      await conversation.startSession({
        agentId: config.agentId,
        ...(useWs
          ? { signedUrl: fresh.signedUrl, connectionType: "websocket" as const }
          : { conversationToken: fresh.conversationToken, connectionType: "webrtc" as const }),
        dynamicVariables,
      } as any);
      setStarted(true);
      // Immediately capture the EL conversation id so the summarize-conversation
      // fallback can fetch the official transcript if our client-side turn
      // capture misses anything. linkElId is idempotent + queues if the DB row
      // isn't ready yet.
      try {
        const elId = (conversation as any)?.getId?.();
        if (elId) linkElId(String(elId), readyConvId);
      } catch (e) {
        console.warn("[charles] post-start getId failed", e);
      }
      // Send grounding as contextual update so it does NOT overwrite the
      // persona prompt configured in ElevenLabs.
      try {
        (conversation as any).sendContextualUpdate?.(groundingNote);
      } catch (e) {
        console.warn("[charles] grounding contextual update failed", e);
      }
    } catch (e) {
      console.error("[charles] startSession failed", e);
    }

  }, [session, config, voiceOptIn, forceTextOnly, conversation, linkElId, readyConvId]);

  const stop = useCallback(async () => {
    const convId = readyConvId;
    await conversation.endSession();
    setStarted(false);
    if (convId) {
      // Mark the conversation as ended in our DB so ConversationsList and the
      // summarizer both have a clean signal. The agent-tool path already calls
      // this RPC via dispatch("end_conversation"); the user-clicks-End path
      // (this branch) previously didn't, leaving every row "open" forever.
      try {
        const { error } = await supabase.rpc("end_conversation", {
          _conversation_id: convId,
        });
        if (error) console.warn("[charles] end_conversation rpc failed", error);
      } catch (e) {
        console.warn("[charles] end_conversation threw", e);
      }
      void summarizeConversation(convId);
    }
  }, [conversation, readyConvId]);

  // Keep stopRef pointed at the latest stop() so the end_conversation client
  // tool can invoke the real hangup logic without recreating clientTools.
  useEffect(() => {
    stopRef.current = stop;
  }, [stop]);

  const sendText = useCallback(
    (text: string) => {
      if (!text.trim()) return;
      conversation.sendUserMessage(text.trim());
    },
    [conversation],
  );

  const value = useMemo<CharlesContextValue>(
    () => ({
      session,
      conversation,
      configError: config.error,
      started,
      forceTextOnly,
      micDenied,
      voiceOptIn,
      start,
      stop,
      sendText,
    }),
    [session, conversation, config.error, started, forceTextOnly, micDenied, voiceOptIn, start, stop, sendText],
  );

  return <CharlesContext.Provider value={value}>{children}</CharlesContext.Provider>;
}
